import {
  ChangeDetectionStrategy,
  Component,
  DestroyRef,
  Injector,
  afterNextRender,
  computed,
  inject,
  signal,
  viewChild,
} from '@angular/core';
import type { ElementRef } from '@angular/core';
import { RouterLink, RouterLinkActive, RouterOutlet } from '@angular/router';
import { AuthService } from './auth.service';
import { MngIconComponent } from './shared/icon/icon.component';

const DESKTOP_QUERY = '(min-width: 48rem)';

@Component({
  selector: 'mng-shell',
  imports: [RouterLink, RouterLinkActive, RouterOutlet, MngIconComponent],
  changeDetection: ChangeDetectionStrategy.OnPush,
  templateUrl: './shell.component.html',
  styleUrl: './shell.component.scss',
  host: {
    class: 'mng-forms',
    '(document:keydown.escape)': 'closeMenu()',
  },
})
export class ShellComponent {
  protected readonly auth = inject(AuthService);
  private readonly menuButton =
    viewChild<ElementRef<HTMLButtonElement>>('menuButton');

  private readonly main = viewChild<ElementRef<HTMLElement>>('main');
  private readonly firstNavLink =
    viewChild<ElementRef<HTMLElement>>('firstNavLink');
  private readonly injector = inject(Injector);

  protected readonly menuOpen = signal(false);
  protected readonly desktop = signal(false);
  protected readonly drawerInert = computed(
    () => !this.desktop() && !this.menuOpen(),
  );
  protected readonly pageInert = computed(
    () => !this.desktop() && this.menuOpen(),
  );
  protected readonly username = computed(() => this.auth.identity()?.username);
  protected readonly initial = computed(
    () => this.username()?.charAt(0).toUpperCase() ?? '',
  );

  constructor() {
    if (typeof window.matchMedia !== 'function') return;
    const query = window.matchMedia(DESKTOP_QUERY);
    this.desktop.set(query.matches);
    const onChange = (e: MediaQueryListEvent) => {
      this.desktop.set(e.matches);
      if (e.matches) this.menuOpen.set(false);
    };
    query.addEventListener('change', onChange);
    inject(DestroyRef).onDestroy(() =>
      query.removeEventListener('change', onChange),
    );
  }

  protected toggleMenu() {
    this.menuOpen.update((open) => !open);
    if (this.menuOpen()) this.focusAfterRender(this.firstNavLink);
  }

  protected closeMenu() {
    if (!this.menuOpen()) return;
    this.menuOpen.set(false);
    this.focusAfterRender(this.menuButton);
  }

  // The target is inert until the next render drops the attribute, and inert elements refuse focus.
  private focusAfterRender(target: () => ElementRef<HTMLElement> | undefined) {
    afterNextRender(() => target()?.nativeElement.focus(), {
      injector: this.injector,
    });
  }

  protected skipToMain(event: Event) {
    event.preventDefault();
    this.main()?.nativeElement.focus();
  }

  protected chooseLink() {
    this.menuOpen.set(false);
  }
}
